import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormGroup, FormArray, Validators, FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { PrimeNgModule } from '../shared/prime-ng.module';
import { RouterModule } from '@angular/router';
import { ProjectService } from '../services/project.service';
import { ClientService } from '../services/client.service';
import { APIUrl } from '../constant/api-call';
import { firstValueFrom } from 'rxjs';
import moment from 'moment';
import { FormExcelExport, ExportSelection } from '../services/form-excel-export';
import { FormExcelImport, ParsedPeriod } from '../services/form-excel-import';

@Component({
  selector: 'app-dynamic-entry-form',
  standalone: true,
  imports: [CommonModule, PrimeNgModule, FormsModule, ReactiveFormsModule, RouterModule],
  templateUrl: './dynamic-entry-form.component.html',
  styleUrls: ['./dynamic-entry-form.component.css']
})
export class DynamicEntryFormComponent implements OnInit {
  @Output() formSaved = new EventEmitter<boolean>();
  @Output() formNameChanged = new EventEmitter<string>();
  public dynamicForm: FormGroup = new FormGroup({});
  public formSelectControl: FormControl = new FormControl(null);
  public showActionBar = true;
  public formConfig: any = null;
  public activeSection: string = '';
  public activeTabIndex: number = 0;
  public matrixSections: any[] = [];
  public selectedYear: number = 0;
  public selectedMonth: number = 0;
  public selected_form: number | null = null;
  public editSubmissionId: number | null = null;
  public showExcelDialog = false;
  public excelDateOrMonthOrYearHas: any = null;
  public excelOptionsError = '';
  public excelIncludeDate = false;

  public readonly NO_YEAR_KEY = 0;
  public yearOptions: { label: string; value: number }[] = [];
  public monthOptions: { label: string; value: number }[] = [
    { label: 'January', value: 1 },
    { label: 'February', value: 2 },
    { label: 'March', value: 3 },
    { label: 'April', value: 4 },
    { label: 'May', value: 5 },
    { label: 'June', value: 6 },
    { label: 'July', value: 7 },
    { label: 'August', value: 8 },
    { label: 'September', value: 9 },
    { label: 'October', value: 10 },
    { label: 'November', value: 11 },
    { label: 'December', value: 12 }
  ];
  public selectedExcelYears: number[] = [];
  public excelYearMonths: { [year: number]: number[] } = {};
  public excelMonthDates: { [yearKey: number]: { [month: number]: number[] } } = {};
  private readonly EMPTY_DATES: number[] = [];
  private dateOptionsCache: { [days: number]: { label: string; value: number }[] } = {};

  public importedPeriods: ParsedPeriod[] = [];
  public activePeriodIndex = 0;
  private periodSnapshots: { [i: number]: any } = {};

  public commonDataObject = {
    APIObj: APIUrl,
    selectedMaster: null,
    issuccessAlert: false,
    iserrorAlert: false,
    apiMeassge: '',
  }
  public excelImportIssues: any[] = [];
  public showIssuesDialog = false;

  constructor(
    public projectService: ProjectService,
    private clientService: ClientService,
    public cr: ChangeDetectorRef,
    public formExcelExport: FormExcelExport,
    public formExcelImport: FormExcelImport
  ) {
    this.buildYearOptions();
    const dynamicData = this.projectService.getDynamicEntryFormData();
    if (dynamicData) {
      this.loadSubmissionForEdit(dynamicData);
    } else {
      this.autoSelectSingleForm();
    }
    this.projectService.getChangesProject().subscribe(data => {
      if (data) {
        this.resetForm();
      }
    });
  }

  async ngOnInit() {
    await this.projectService.getMasterData();
    this.getMasterAndGridData();
  }

  private buildYearOptions() {
    const current = new Date().getFullYear();
    const from = 2000;
    const to = current + 5;
    this.yearOptions = [];
    for (let y = from; y <= to; y++) {
      this.yearOptions.push({ label: String(y), value: y });
    }
  }

  autoSelectSingleForm() {
    const forms = this.projectService.getActiveFormList();
    if (forms?.length > 0) {
      this.formSelectControl.setValue(forms[0].form_id);
      this.selected_form = forms[0].form_id;
      this.onFormChange();
    }
  }

  getMasterAndGridData() {
    const projectId = this.projectService.selectedProject()?.project_id;
    if (projectId) {
      this.projectService.getFormsProjectWise(projectId);
    }
  }

  getFieldOptions(field: any): any[] {
    const masterData = this.projectService?.getMasterList();
    let dropdownData: any[] = [];
    if (field?.master_value_id) {
      masterData.forEach((e) => {
        if (e?.master_value_id === field?.master_value_id) {
          dropdownData = e?.sub_master_values_details
            .filter((sub: any) => sub.is_active)
            .map((sub: any) => ({
              label: sub.sub_master_value_name,
              value: sub.sub_master_value_id
            }));
        }
      });
      return dropdownData;
    } else {
      return [];
    }
  }

  getMasterName(row: any): string {
    const masterList = this.projectService.getActiveMasterList();
    if (!masterList?.length) return '';
    const master = masterList.find(m => m.master_value_id === row.master_value_id);
    if (!master) return '';
    if (row.sub_master_value_id && master.sub_master_values_details?.length) {
      const subMaster = master.sub_master_values_details.find(
        (sm: any) => sm.sub_master_value_id === row.sub_master_value_id
      );
      if (subMaster) return master.master_value_name + " (" + subMaster.sub_master_value_name + ")";
    }
    return master.master_value_name;
  }

  getSubMasterOptions(row: any, j?: any, i?: any, name?: any): any {
    const masterList = this.projectService.getActiveMasterList();
    const master = masterList.find(m => m.master_value_id === row?.master_value_id) || [];
    return master?.sub_master_values_details || [];
  }

  async onFormChange() {
    const selectedFormId = this.formSelectControl.value;
    if (selectedFormId) {
      this.selected_form = selectedFormId;
      this.formConfig = await firstValueFrom(this.projectService.getFormDetails(this.selected_form));
      const simpleLayoutData = this.formConfig?.sections?.filter((e: any) => e?.layout_type === 'Simple');
      if (simpleLayoutData?.length > 0) {
        this.excelDateOrMonthOrYearHas = this.getSimpleSectionHeaderFlags(simpleLayoutData[0]);
      }
      this.dynamicForm = this.createForm(this.formConfig);
      this.matrixSections = this.formConfig?.sections?.filter((s: any) => s.layout_type === 'Matrix') || [];
      this.activeTabIndex = 0;
      this.excelImportIssues = [];
      this.importedPeriods = [];
      this.periodSnapshots = {};
      this.activePeriodIndex = 0;
      this.activeSection = this.matrixSections?.length ? this.matrixSections[0].section_name : '';
      this.formNameChanged.emit(this.formConfig?.form_name || 'Submission Form');
    }
  }

  createForm(config: any): FormGroup {
    const group: any = {};
    config?.sections?.forEach((section: any) => {
      if (section.layout_type === 'Simple') {
        section.fields.forEach((field: any) => {
          group[field.field_key] = new FormControl(
            '',
            field.is_required ? Validators.required : null
          );
        });
      } else if (section.layout_type === 'Matrix') {
        group[section.section_name] = new FormArray(
          section.matrix_rows.map((row: any, index: number) => this.createMatrixRow(section.fields, row))
        );
      }
    });
    return new FormGroup(group);
  }

  createMatrixRow(fields: any[], row: any): FormGroup {
    const group: any = {};
    let sub_masters = this.getSubMasterOptions(row);
    if (sub_masters?.length > 0) {
      row['sub_masters'] = sub_masters;
      group['sub_master_values'] = new FormControl([]);
    }
    fields.forEach(field => {
      group[field.field_key] = new FormControl(
        row?.[field.field_key] ?? '',
        field.is_required ? [Validators.required] : []
      );
    });
    if (row.is_remark) {
      group['remark'] = new FormControl(row?.remark || '');
    }
    return new FormGroup(group);
  }

  getMatrix(sectionName: string): FormArray {
    const matrix = this.dynamicForm.get(sectionName);
    return (matrix as FormArray) ?? new FormArray([]);
  }

  setActiveTab(index: number, section_name: string) {
    this.activeTabIndex = index;
    this.activeSection = section_name;
  }

  moveBack() {
    if (this.activeTabIndex > 0) {
      this.activeTabIndex = this.activeTabIndex - 1;
      this.activeSection = this.matrixSections[this.activeTabIndex].section_name;
    }
  }

  saveNext() {
    if (this.activeTabIndex < this.matrixSections.length - 1) {
      this.activeTabIndex = this.activeTabIndex + 1;
      this.activeSection = this.matrixSections[this.activeTabIndex].section_name;
    }
  }

  onYearChange(year: Date) {
    this.selectedYear = year.getFullYear();
    this.dynamicForm.patchValue({
      month: null,
      date: null
    });
  }

  onMonthChange(month: Date) {
    this.selectedMonth = month.getMonth();
    this.dynamicForm.patchValue({
      date: new Date(this.selectedYear, this.selectedMonth)
    });
  }

  getDefaultMonth(): Date | null {
    if (this.selectedYear) {
      return new Date(this.selectedYear, 0, 1);
    }
    return null;
  }

  formatValueByType(value: any, fieldType: string): any {
    if (!value) return value;
    const type = fieldType?.toLowerCase();
    const m = moment(value);
    if (!m.isValid()) return value;
    if (type === 'date') {
      return m.format('DD');
    }
    if (type === 'month') {
      return m.format('MM');
    }
    if (type === 'year') {
      return m.format('YYYY');
    }
    return value;
  }

  getFieldKeyByFieldId(fieldId: number): string {
    for (let section of this.formConfig?.sections) {
      const field = section?.fields.find(
        (f: any) => f?.field_id === fieldId
      );
      if (field) return field.field_key;
    }
    return '';
  }

  getFieldById(fieldId: number): any {
    for (let section of this.formConfig.sections) {
      const field = section.fields.find((f: any) => f.field_id === fieldId);
      if (field) return field;
    }
    return null;
  }

  hasRemarkColumn(section: any): boolean {
    return section.matrix_rows?.some((row: any) => row.is_remark) || false;
  }

  async loadSubmissionForEdit(submission: any) {
    const sub = submission?.submissions?.length
      ? submission.submissions[0]
      : submission;

    this.editSubmissionId =
      sub?.form_submission_id
      ?? submission?.form_submission_id
      ?? submission?.submissions?.[0]?.form_submission_id
      ?? null;

    this.formSelectControl.setValue(submission.form_id);
    this.selected_form = submission.form_id;
    await this.onFormChange();

    const values = sub?.form_submission_values || [];
    const remarks = sub?.form_submission_remarks || [];

    values.forEach((v: any) => {
      if (!v.section_matrix_row_id) {
        const fieldKey = this.getFieldKeyByFieldId(v.field_id);
        if (!fieldKey) return;
        let value = v?.value;
        const field = this.getFieldById(v.field_id);
        if (field && value) {
          field.form_submission_value_id = v.form_submission_value_id;
          const type = field.field_type.toLowerCase();
          if (type === 'dropdown') {
            value = Number(v.value);
          }
          if (type === 'year') {
            const year = +value;
            this.selectedYear = year;
            value = new Date(year, 0, 1);
          }
          if (type === 'month') {
            const monthIndex = (+value) - 1;
            this.selectedMonth = monthIndex;
            value = new Date(this.selectedYear || 2020, monthIndex, 1);
          }
          if (type === 'date') {
            const day = +value;
            value = new Date(this.selectedYear, this.selectedMonth, day);
          }
          this.dynamicForm.get(fieldKey)?.setValue(value);
        }
      }
    });

    this.formConfig.sections
      .filter((s: any) => s.layout_type === 'Matrix')
      .forEach((section: any) => {
        const formArray = this.getMatrix(section.section_name);
        formArray.controls.forEach((rowGroup: any, rowIndex: number) => {
          const matrixRow = section.matrix_rows[rowIndex];
          const rowValues = values.filter(
            (x: any) => x.section_matrix_row_id === matrixRow.section_matrix_row_id
          );
          rowValues.forEach((rv: any) => {
            const field = section.fields.find((f: any) => f.field_id === rv.field_id);
            if (field) {
              field.form_submission_value_id = rv.form_submission_value_id;
              const fieldKey = field.field_key;
              if (fieldKey && rowGroup.get(fieldKey)) {
                rowGroup.get(fieldKey)?.setValue(rv.value);
              }
            }
            if (rv?.sub_master_values?.length > 0 && rowGroup.get('sub_master_values')) {
              let selectedIds: any[] = [];
              rv?.sub_master_values.forEach((e: any) => {
                selectedIds.push(e?.sub_master_value_id);
              });
              rowGroup.get('sub_master_values')?.setValue(selectedIds);
            }
          });
          const remarkObj = remarks.find(
            (r: any) => r.section_matrix_row_id === matrixRow.section_matrix_row_id
          );
          if (remarkObj && rowGroup.get('remark')) {
            matrixRow.remark_id = remarkObj.remark_id ?? null;
            rowGroup.get('remark')?.setValue(remarkObj.remarks);
          }
        });
      });
    this.cr.detectChanges();
  }

  private buildSubmissionEntry(): {
    form_submission_id: number | null;
    form_submission_values: any[];
    form_submission_remarks: any[];
  } {
    const answers: any[] = [];
    const remarks: any[] = [];

    this.formConfig.sections.forEach((section: any) => {
      if (section.layout_type === 'Simple') {
        section.fields.forEach((field: any) => {
          answers.push({
            form_submission_value_id: field.form_submission_value_id ?? null,
            field_id: field?.field_id,
            section_matrix_row_id: null,
            value: this.formatValueByType(
              this.dynamicForm?.value[field?.field_key],
              field?.field_type
            )?.toString()
          });
        });
      }

      if (section.layout_type === 'Matrix') {
        const matrixValues = this.dynamicForm.value[section.section_name];
        matrixValues.forEach((rowValue: any, rowIndex: number) => {
          const matrixRow = section.matrix_rows[rowIndex];
          section.fields.forEach((field: any) => {
            const value = rowValue[field.field_key];
            if (
              matrixRow?.sub_masters &&
              matrixRow?.sub_masters?.length > 0 &&
              rowValue.sub_master_values?.length > 0
            ) {
              answers.push({
                form_submission_value_id: field?.form_submission_value_id ?? null,
                field_id: field.field_id,
                section_matrix_row_id: matrixRow.section_matrix_row_id,
                value: value?.toString(),
                sub_master_values: rowValue?.sub_master_values
              });
            } else {
              answers.push({
                form_submission_value_id: field?.form_submission_value_id ?? null,
                field_id: field?.field_id,
                section_matrix_row_id: matrixRow?.section_matrix_row_id,
                value: value?.toString()
              });
            }
          });
          if (matrixRow.is_remark && rowValue.remark) {
            remarks.push({
              remark_id: matrixRow.remark_id ?? null,
              section_matrix_row_id: matrixRow.section_matrix_row_id,
              remarks: rowValue.remark
            });
          }
        });
      }
    });

    return {
      form_submission_id: this.editSubmissionId,
      form_submission_values: answers,
      form_submission_remarks: remarks
    };
  }

  generatePayload(arg?: any, extraSubmissions?: any[]): any {
    const submissions = arg
      ? (extraSubmissions?.length ? extraSubmissions : [])
      : (extraSubmissions?.length ? extraSubmissions : [this.buildSubmissionEntry()]);

    return {
      form_id: this.formConfig.form_id,
      is_delete: arg ? true : false,
      submissions
    };
  }

  submitForm(arg?: any) {
    if (this.dynamicForm.invalid) {
      this.dynamicForm.markAllAsTouched();
      return;
    }
    this.projectService.loading.set(true);
    const payload = this.generatePayload(arg);

    this.clientService.postAPI(this.commonDataObject.APIObj.SUBMISSION_FORM_UPSERT, payload).subscribe(
      async (res: any) => {
        // Normalize: API may return single object OR array (multi submissions)
        const responses: any[] = Array.isArray(res) ? res : [res];
        const first = responses[0];

        const allOk = responses.length > 0 &&
          responses.every(r => r && r.code === 'CSR_MS_OK_200' && r.error_status === false);

        if (allOk) {
          this.formSaved.emit(true);
          await this.getMasterAndGridData();
          this.resetForm();
          this.commonDataObject.apiMeassge = first?.message || 'Saved successfully';
          this.commonDataObject.issuccessAlert = true;
          this.commonDataObject.iserrorAlert = false;
        } else {
          const msg = responses
            .filter(r => r && r.code !== 'CSR_MS_OK_200')
            .map(r => r?.message)
            .filter(Boolean)
            .join(', ') || 'Save failed';
          this.commonDataObject.apiMeassge = msg;
          this.commonDataObject.iserrorAlert = true;
          this.commonDataObject.issuccessAlert = false;
        }

        setTimeout(() => {
          this.projectService.loading.set(false);
          this.commonDataObject.issuccessAlert = false;
          this.commonDataObject.iserrorAlert = false;
        }, 2000);
      },
      (error) => {
        this.commonDataObject.iserrorAlert = true;
        this.commonDataObject.apiMeassge = error.error?.message || 'An error occurred while saving';
        setTimeout(() => {
          this.projectService.loading.set(false);
          this.commonDataObject.issuccessAlert = false;
          this.commonDataObject.iserrorAlert = false;
        }, 2000);
      }
    );
  }

  resetForm() {
    this.editSubmissionId = null;
    this.selected_form = null;
    this.formSelectControl.reset();
    this.formConfig = null;
    this.matrixSections = [];
    this.selectedYear = 0;
    this.selectedMonth = 0;
    this.dynamicForm.reset();
    this.activeTabIndex = 0;
    this.activeSection = this.matrixSections[0]?.section_name ?? '';
    this.excelDateOrMonthOrYearHas = null;
    this.excelImportIssues = [];
    this.showIssuesDialog = false;
    this.showExcelDialog = false;
    this.excelOptionsError = '';
    this.excelIncludeDate = false;
    this.excelMonthDates = {};
    this.selectedExcelYears = [];
    this.excelYearMonths = {};
    this.importedPeriods = [];
    this.periodSnapshots = {};
    this.activePeriodIndex = 0;
    this.formNameChanged.emit('Submission Form');
  }

  getSimpleSectionHeaderFlags(section: any): any {
    const result = { hasYear: 0, hasMonth: 0, hasDate: 0 };
    const fields: any[] = Array.isArray(section.fields) ? section.fields : [];
    for (const f of fields) {
      if (!f || typeof f !== 'object') continue;

      const candidates = [
        String(f.field_key ?? '').toLowerCase(),
        String(f.field_label ?? '').toLowerCase(),
        String(f.field_type ?? '').toLowerCase()
      ];

      if (candidates.some(c => c === 'year')) result.hasYear = 1;
      if (candidates.some(c => c === 'month')) result.hasMonth = 1;
      if (candidates.some(c => c === 'date' || c === 'day')) result.hasDate = 1;
    }

    return result;
  }

  openExcelDialog() {
    if (!this.selected_form) return;

    const flags = this.excelDateOrMonthOrYearHas;
    const hasYear = !!flags?.hasYear;
    const hasMonth = !!flags?.hasMonth;
    const hasDate = !!flags?.hasDate;

    if (!hasYear && !hasMonth && !hasDate) {
      this.runExcelExport([], false);
      return;
    }

    this.excelIncludeDate = hasDate;
    this.excelMonthDates = {};

    this.selectedExcelYears = [];
    this.excelYearMonths = hasYear ? {} : { [this.NO_YEAR_KEY]: this.allMonthValues() };
    this.excelOptionsError = '';
    this.showExcelDialog = true;
  }

  private allMonthValues(): number[] {
    return this.monthOptions.map(m => m.value);
  }

  onExcelYearsChange() {
    this.selectedExcelYears = [...(this.selectedExcelYears || [])].sort((a, b) => a - b);

    const next: { [year: number]: number[] } = {};
    this.selectedExcelYears.forEach(y => {
      next[y] = this.excelYearMonths[y] ?? this.allMonthValues();
    });
    this.excelYearMonths = next;
    this.excelOptionsError = '';
  }

  onExcelMonthsChange() {
    this.excelOptionsError = '';
  }

  getSelectedMonths(yearKey: number): { label: string; value: number }[] {
    const selected = this.excelYearMonths[yearKey] ?? [];
    return this.monthOptions.filter(m => selected.includes(m.value));
  }

  private getDaysInMonth(yearKey: number, month: number): number {
    if (yearKey === this.NO_YEAR_KEY) {
      if (month === 2) return 29;
      return [4, 6, 9, 11].includes(month) ? 30 : 31;
    }
    return new Date(yearKey, month, 0).getDate();
  }

  getDateOptions(yearKey: number, month: number): { label: string; value: number }[] {
    const days = this.getDaysInMonth(yearKey, month);
    if (!this.dateOptionsCache[days]) {
      this.dateOptionsCache[days] = Array.from({ length: days }, (_, i) => ({
        label: String(i + 1),
        value: i + 1
      }));
    }
    return this.dateOptionsCache[days];
  }

  getMonthDates(yearKey: number, month: number): number[] {
    return this.excelMonthDates[yearKey]?.[month] ?? this.EMPTY_DATES;
  }

  setMonthDates(yearKey: number, month: number, dates: number[]) {
    if (!this.excelMonthDates[yearKey]) this.excelMonthDates[yearKey] = {};
    this.excelMonthDates[yearKey][month] = [...(dates || [])].sort((a, b) => a - b);
    this.excelOptionsError = '';
  }

  private buildMonthDates(yearKey: number, months: number[]): { [month: number]: number[] } {
    const result: { [month: number]: number[] } = {};
    months.forEach(m => {
      const max = this.getDaysInMonth(yearKey, m);
      result[m] = [...new Set(this.getMonthDates(yearKey, m))]
        .filter(d => Number.isInteger(d) && d >= 1 && d <= max)
        .sort((a, b) => a - b);
    });
    return result;
  }

  trackByYear(_index: number, year: number) {
    return year;
  }

  trackByMonth(_index: number, month: { value: number }) {
    return month.value;
  }

  confirmExcelExport() {
    const flags = this.excelDateOrMonthOrYearHas;
    const hasYear = !!flags?.hasYear;
    const hasMonth = !!flags?.hasMonth;
    const hasDate = !!flags?.hasDate;

    if (hasYear && !this.selectedExcelYears.length) {
      this.excelOptionsError = 'Please select at least one year.';
      return;
    }

    if (hasMonth) {
      const keys = hasYear ? this.selectedExcelYears : [this.NO_YEAR_KEY];
      const missing = keys.filter(k => !(this.excelYearMonths[k]?.length));
      if (missing.length) {
        this.excelOptionsError = hasYear
          ? `Please select at least one month for: ${missing.join(', ')}.`
          : 'Please select at least one month.';
        return;
      }
    }

    if (hasMonth && hasDate) {
      const keys = hasYear ? this.selectedExcelYears : [this.NO_YEAR_KEY];
      for (const k of keys) {
        for (const m of this.getSelectedMonths(k)) {
          if (!this.buildMonthDates(k, [m.value])[m.value].length) {
            this.excelOptionsError =
              `Please select at least one date for ${m.label}${hasYear ? ' ' + k : ''}.`;
            return;
          }
        }
      }
    }

    let selections: ExportSelection[];
    if (hasYear) {
      selections = this.selectedExcelYears.map(y => {
        const months = hasMonth ? [...(this.excelYearMonths[y] ?? [])].sort((a, b) => a - b) : [];
        return {
          year: y,
          months,
          monthDates: hasDate ? this.buildMonthDates(y, months) : undefined
        };
      });
    } else {
      const months = [...(this.excelYearMonths[this.NO_YEAR_KEY] ?? [])].sort((a, b) => a - b);
      selections = [{
        year: null,
        months,
        monthDates: hasDate ? this.buildMonthDates(this.NO_YEAR_KEY, months) : undefined
      }];
    }

    this.excelOptionsError = '';
    this.showExcelDialog = false;
    this.runExcelExport(selections, hasDate);
  }

  closeExcelDialog() {
    this.showExcelDialog = false;
    this.excelOptionsError = '';
  }

  private runExcelExport(selections: ExportSelection[], includeDate: boolean) {
    const excelExportResult = this.formExcelExport.exportTemplate({
      formDefinition: this.formConfig,
      selections,
      includeDate,
      fileName: this.formConfig?.form_name
    });

    if (excelExportResult.success) {
      this.commonDataObject.apiMeassge = excelExportResult.message;
      this.commonDataObject.issuccessAlert = true;
    } else {
      this.commonDataObject.apiMeassge = excelExportResult.message;
      this.commonDataObject.iserrorAlert = true;
    }
    setTimeout(() => {
      this.commonDataObject.issuccessAlert = false;
      this.commonDataObject.iserrorAlert = false;
    }, 4000);
  }

  uploadExcelData() {
    if (!this.selected_form) {
      this.commonDataObject.apiMeassge = 'Please select a form first.';
      this.commonDataObject.iserrorAlert = true;
      setTimeout(() => { this.commonDataObject.iserrorAlert = false; }, 2500);
      return;
    }

    const fileInput = document.getElementById('excelFileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
      fileInput.click();
    }
  }

  async onExcelFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    if (!/\.xlsx?$/i.test(file.name)) {
      this.showError('Please upload a valid .xlsx or .xls file.');
      input.value = '';
      return;
    }

    this.projectService.loading.set(true);

    try {
      const buffer = await file.arrayBuffer();
      const result = this.formExcelImport.parseExcel(buffer, this.formConfig);

      this.excelImportIssues = result.issues || [];
      if (this.excelImportIssues.length) {
        this.showIssuesDialog = true;
      }
      if (result.fatal || !result.parsed) {
        this.showError(result.message);
        console.warn('[Excel Import Issues]', result.issues);
        return;
      }

      this.importedPeriods = result.periods?.length ? result.periods : [result.parsed];
      this.periodSnapshots = {};
      if (this.importedPeriods.length > 1) {
        this.editSubmissionId = null;
      }
      this.loadPeriod(0);

      const hasWarnings = this.excelImportIssues.some(i => i.severity === 'warning');
      const hasErrors = this.excelImportIssues.some(i => i.severity === 'error');

      if (hasErrors || hasWarnings) {
        const total = this.excelImportIssues.filter(
          i => i.severity === 'error' || i.severity === 'warning'
        ).length;

        this.commonDataObject.apiMeassge =
          `Excel loaded with ${total} issue(s). Please review the list below the form.`;
        this.commonDataObject.iserrorAlert = true;
        this.commonDataObject.issuccessAlert = false;

        console.warn('[Excel Import Issues]', this.excelImportIssues);
      } else {
        this.commonDataObject.apiMeassge = this.importedPeriods.length > 1
          ? `Excel loaded with ${this.importedPeriods.length} periods. Review each tab and click Save All.`
          : 'Excel data loaded into the form. Please review and save.';
        this.commonDataObject.issuccessAlert = true;
        this.commonDataObject.iserrorAlert = false;
      }

    } catch (err) {
      this.commonDataObject.apiMeassge =
        `Failed to read file: ${err instanceof Error ? err.message : String(err)}`;
      this.commonDataObject.iserrorAlert = true;
    } finally {
      this.projectService.loading.set(false);
      setTimeout(() => {
        this.commonDataObject.issuccessAlert = false;
        this.commonDataObject.iserrorAlert = false;
      }, 6000);
    }
  }

  loadPeriod(i: number) {
    if (!this.formConfig || !this.importedPeriods[i]) return;
    this.dynamicForm = this.createForm(this.formConfig);
    this.selectedYear = 0;
    this.selectedMonth = 0;
    this.applyParsedDataToForm(this.importedPeriods[i]);
    if (this.periodSnapshots[i]) {
      this.dynamicForm.patchValue(this.periodSnapshots[i]);
    }
    this.activePeriodIndex = i;
    this.cr.detectChanges();
  }

  switchPeriod(i: number) {
    if (i === this.activePeriodIndex) return;
    this.periodSnapshots[this.activePeriodIndex] = this.dynamicForm.getRawValue();
    this.loadPeriod(i);
  }

  async saveAllPeriods() {
    if (this.importedPeriods.length < 2) {
      this.submitForm();
      return;
    }

    const originalIndex = this.activePeriodIndex;
    this.periodSnapshots[originalIndex] = this.dynamicForm.getRawValue();

    this.projectService.loading.set(true);

    const failed: string[] = [];
    const submissionEntries: any[] = [];
    const submissionLabels: string[] = [];

    try {
      for (let i = 0; i < this.importedPeriods.length; i++) {
        const label = this.importedPeriods[i].label;
        this.loadPeriod(i);

        if (this.dynamicForm.invalid) {
          failed.push(`${label} (required fields missing)`);
          continue;
        }

        submissionEntries.push(this.buildSubmissionEntry());
        submissionLabels.push(label);
      }

      if (!submissionEntries.length) {
        this.loadPeriod(originalIndex);
        this.commonDataObject.apiMeassge = `Nothing to save. Failed: ${failed.join(', ')}`;
        this.commonDataObject.iserrorAlert = true;
        this.commonDataObject.issuccessAlert = false;
        return;
      }

      const payload = this.generatePayload(false, submissionEntries);
      const res: any = await firstValueFrom(
        this.clientService.postAPI(this.commonDataObject.APIObj.SUBMISSION_FORM_UPSERT, payload)
      );

      const isOk = !!res && res.code === 'CSR_MS_OK_200' && res.error_status === false;

      if (!isOk) {
        this.loadPeriod(originalIndex);
        const reason = res?.message || 'Unknown error';
        const failureDetails = [...failed, ...submissionLabels.map(l => `${l} (${reason})`)];
        this.commonDataObject.apiMeassge = `Save failed: ${failureDetails.join(', ')}`;
        this.commonDataObject.iserrorAlert = true;
        this.commonDataObject.issuccessAlert = false;
        return;
      }

      const savedIds: number[] = (Array.isArray(res?.data) ? res.data : [])
        .map((d: any) => d?.form_submission_id)
        .filter((id: any) => id !== null && id !== undefined);

      const savedCount = savedIds.length > 0 ? savedIds.length : submissionEntries.length;
      const notSavedByApi = Math.max(submissionEntries.length - savedCount, 0);

      this.formSaved.emit(true);
      await this.getMasterAndGridData();

      const problems: string[] = [...failed];
      if (notSavedByApi > 0) {
        problems.push(`${notSavedByApi} entr${notSavedByApi === 1 ? 'y' : 'ies'} not confirmed by the server`);
      }

      this.resetForm();
      if (problems.length === 0) {
        this.commonDataObject.apiMeassge =
          `${savedCount} ${savedCount === 1 ? 'entry' : 'entries'} saved successfully.`;
        this.commonDataObject.issuccessAlert = true;
        this.commonDataObject.iserrorAlert = false;
      } else {
        this.commonDataObject.apiMeassge =
          `Saved ${savedCount}. Failed: ${problems.join(', ')}`;
        this.commonDataObject.iserrorAlert = true;
        this.commonDataObject.issuccessAlert = false;
      }
    } catch (e: any) {
      this.loadPeriod(originalIndex);
      this.commonDataObject.apiMeassge = e?.error?.message || 'Error while saving';
      this.commonDataObject.iserrorAlert = true;
      this.commonDataObject.issuccessAlert = false;
    } finally {
      this.projectService.loading.set(false);
      setTimeout(() => {
        this.commonDataObject.issuccessAlert = false;
        this.commonDataObject.iserrorAlert = false;
      }, 5000);
    }
  }

  private applyParsedDataToForm(parsed: any) {
    if (!this.formConfig || !parsed?.sections?.length) return;

    const simpleSection = this.formConfig.sections.find(
      (s: any) => s.layout_type === 'Simple'
    );

    if (simpleSection) {
      const yearField = simpleSection.fields.find(
        (f: any) => (f.field_key === 'year') || ((f.field_type || '').toLowerCase() === 'year')
      );
      const monthField = simpleSection.fields.find(
        (f: any) => (f.field_key === 'month') || ((f.field_type || '').toLowerCase() === 'month')
      );
      const dateField = simpleSection.fields.find(
        (f: any) => (f.field_key === 'date') || ((f.field_type || '').toLowerCase() === 'date')
      );

      const yearNum = parsed.year != null && parsed.year !== ''
        ? parseInt(String(parsed.year), 10)
        : null;

      const monthNum = typeof parsed.month === 'number' ? parsed.month : null;

      const dateNum = parsed.date != null && parsed.date !== ''
        ? parseInt(String(parsed.date), 10)
        : null;

      if (yearField && yearNum != null && !isNaN(yearNum)) {
        this.selectedYear = yearNum;
        this.dynamicForm.get(yearField.field_key)?.setValue(new Date(yearNum, 0, 1));
      }
      if (monthField && monthNum != null) {
        this.selectedMonth = monthNum - 1;
        this.dynamicForm.get(monthField.field_key)?.setValue(
          new Date(this.selectedYear || new Date().getFullYear(), this.selectedMonth, 1)
        );
      }
      if (dateField && dateNum != null && !isNaN(dateNum)) {
        this.dynamicForm.get(dateField.field_key)?.setValue(
          new Date(this.selectedYear, this.selectedMonth, dateNum)
        );
      }
    }

    // --- Matrix sections ---
    parsed.sections.forEach((parsedSection: any) => {
      const sectionDef = this.formConfig.sections.find(
        (s: any) => s.section_name === parsedSection.section_name
      );
      if (!sectionDef || sectionDef.layout_type !== 'Matrix') return;

      const formArray = this.getMatrix(sectionDef.section_name);
      if (!formArray) return;

      parsedSection.rows.forEach((parsedRow: any) => {
        const idx = sectionDef.matrix_rows.findIndex(
          (mr: any) =>
            String(mr.master_value_name ?? '').trim().toLowerCase() ===
            parsedRow.master_value_name.trim().toLowerCase()
        );
        if (idx === -1) return;

        const rowGroup = formArray.at(idx) as FormGroup;
        if (!rowGroup) return;

        Object.keys(parsedRow.fields || {}).forEach(fieldKey => {
          const ctrl = rowGroup.get(fieldKey);
          if (ctrl) {
            ctrl.setValue(parsedRow.fields[fieldKey]);
            ctrl.markAsDirty();
            ctrl.updateValueAndValidity();
          }
        });

        if (parsedRow.remark != null && rowGroup.get('remark')) {
          rowGroup.get('remark')?.setValue(parsedRow.remark);
          rowGroup.get('remark')?.markAsDirty();
        }
      });
    });

    this.cr.detectChanges();
  }

  private showError(msg: string) {
    this.commonDataObject.apiMeassge = msg;
    this.commonDataObject.iserrorAlert = true;
    setTimeout(() => { this.commonDataObject.iserrorAlert = false; }, 4000);
  }

  closeIssuesDialog() {
    this.showIssuesDialog = false;
  }

  ngOnDestroy(): void {
    this.resetForm();
    this.projectService.clearData();
    this.projectService.dynamicEntryFormData.set(null);
  }
}